<template>
  <div class="relative isolate bg-white px-6 py-24 sm:py-32 lg:px-8">
    <div class="mx-auto max-w-4xl text-center">
      <h2 class="text-base font-semibold text-blue-600">Planos</h2>
      <p class="mt-2 text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">Escolha o plano ideal para você</p>
    </div>

    <p class="mx-auto mt-6 max-w-2xl text-center text-lg text-gray-600 sm:text-xl">
      Planos flexíveis para otimizar suas campanhas no Google Ads e maximizar seus resultados.
    </p>

    <div class="relative mx-auto mt-16 max-w-6xl">
      <!-- Setas do Carrossel -->
      <button @click="swiper.slidePrev()" class="absolute top-1/2 -left-14 z-10 hidden md:flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 transition transform -translate-y-1/2">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button @click="swiper.slideNext()" class="absolute top-1/2 -right-14 z-10 hidden md:flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 transition transform -translate-y-1/2">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <!-- Carrossel de Planos -->
      <Swiper
        :slidesPerView="1"
        :spaceBetween="20"
        :breakpoints="{
          768: { slidesPerView: 2 },
          1024: { slidesPerView: 3 }
        }"
        @swiper="setSwiper"
        class="py-6"
      >
        <SwiperSlide v-for="tier in tiers" :key="tier.id">
          <div class="border-gray-200 border rounded-3xl p-8 bg-white ring-1 ring-gray-100 text-center shadow-md flex flex-col min-h-[685px]">
            <h3 class="text-xl font-semibold text-blue-600">{{ tier.name }}</h3>
            <p class="mt-4 text-gray-600 text-base">{{ tier.description }}</p>

            <!-- Serviços Mensais -->
            <h4 class="mt-6 font-semibold text-gray-900">📅 Serviços Mensais</h4>
            <ul class="mt-4 space-y-2 text-gray-600 text-sm text-left flex-grow">
              <li v-for="feature in tier.featuresMonthly" :key="feature" class="flex gap-x-2">
                <CheckIcon class="h-5 w-5 text-blue-600 flex-none" />
                {{ feature }}
              </li>
            </ul>

            <!-- Serviços de Projeto -->
            <h4 class="mt-6 font-semibold text-gray-900">🛠️ Serviços de Projeto</h4>
            <ul class="mt-4 space-y-2 text-gray-600 text-sm text-left flex-grow">
              <li v-for="feature in tier.featuresOneTime" :key="feature" class="flex gap-x-2">
                <CheckIcon class="h-5 w-5 text-blue-600 flex-none" />
                {{ feature }}
              </li>
            </ul>

            <div class="mt-auto">
              <a :href="tier.href" class="mt-6 block bg-blue-600 text-white px-6 py-3 rounded-lg shadow-md text-sm font-semibold hover:bg-blue-700 transition">
                🚀 Solicitar Orçamento
              </a>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup>
import { CheckIcon } from '@heroicons/vue/20/solid'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

let swiper
const setSwiper = (instance) => {
  swiper = instance
}

const tiers = [
  {
    name: '🚀 Start',
    id: 'tier-start',
    href: 'https://wa.me/+5519999041297',
    description: "Para quem está começando no tráfego pago.",
    featuresMonthly: [
      'Criação de campanhas no Google Ads',
      'Análise detalhada baseada em KPIs',
      'Grupo de WhatsApp ou Slack (resposta em até 60 min)',
    ],
    featuresOneTime: [
      'Criação de textos persuasivos para anúncios',
    ],
  },
  {
    name: '💎 Pro (Popular)',
    id: 'tier-pro',
    href: 'https://wa.me/+5519999041297',
    description: "Para empresas que querem crescer com estratégia.",
    featuresMonthly: [
      'Criação de campanhas no Google Ads',
      'Otimização e acompanhamento diário',
      'Relatórios personalizados',
      'Auditoria completa de contas de anúncios',
      'Configuração de pixels e APIs de conversão',
      'Reuniões mensais de acompanhamento',
      'Grupo de suporte com resposta em até 60 min',
    ],
    featuresOneTime: [
      'Configuração completa de contas de anúncios (Google Ads, Facebook Business, etc.)',
    ],
  },
  {
    name: '🔥 Premium',
    id: 'tier-premium',
    href: 'https://wa.me/+5519999041297',
    description: "Para quem quer máxima performance e automação.",
    featuresMonthly: [
      'Criação de campanhas no Google Ads',
      'Otimização e acompanhamento diário',
      'Relatórios personalizados',
      'Auditoria completa de contas de anúncios',
      'Configuração de pixels e APIs de conversão',
      'Integração com CRM (RD Station, HubSpot, etc.)',
      'Reuniões mensais de acompanhamento',
      'Grupo de suporte com resposta em até 30 min',
      'Atendimento prioritário',
    ],
    featuresOneTime: [
      'Criação do GMN e otimização',
      'Criação e configuração de Landing Pages',
    ],
  },
  {
    name: '✨ Personalizado',
    id: 'tier-custom',
    href: 'https://wa.me/+5519999041297',
    description: "Escolha os serviços que deseja contratar.",
    featuresMonthly: [
      'Defina o nível de suporte e otimização desejado',
    ],
    featuresOneTime: [
      'Criação de campanhas específicas',
      'Configuração personalizada de tracking e CRM',
      'Outros serviços sob demanda',
    ],
  },
]
</script>
